import type { PortableTextBlock } from "sanity";

const LINK_MARK_TYPES = new Set(["link", "internalLink"]);

interface ISpanChild {
  _type?: string;
  text?: string;
  marks?: string[];
}

interface IMarkDef {
  _key: string;
  _type: string;
}

/**
 * Defensive cleanup of link marks in PortableText blocks before rendering.
 *
 * The linking engine has produced spans carrying two or more overlapping link
 * marks (e.g. an internalLink written inside an existing link span). Rendering
 * those nests <a> tags, which browsers split into an empty anchor plus
 * fragment anchors — the "invisible empty links" class from the SEO crawl
 * (AD-35994). Until the content-side migration lands, keep exactly one link
 * mark per span (prefer internalLink over link; prefer the most specific
 * span), and drop link marks from whitespace-only spans so they cannot render
 * as empty anchors.
 */
export const sanitizeLinkSpans = (
  blocks: PortableTextBlock[],
): PortableTextBlock[] => {
  if (!Array.isArray(blocks)) {
    return blocks;
  }

  return blocks.map((block) => {
    if (
      block?._type !== "block" ||
      !Array.isArray(block.children) ||
      !Array.isArray(block.markDefs)
    ) {
      return block;
    }

    const linkDefs = new Map<string, string>();
    for (const def of block.markDefs as IMarkDef[]) {
      if (def?._key && LINK_MARK_TYPES.has(def._type)) {
        linkDefs.set(def._key, def._type);
      }
    }

    if (!linkDefs.size) {
      return block;
    }

    const usage = new Map<string, number>();
    for (const child of block.children as ISpanChild[]) {
      for (const mark of child?.marks ?? []) {
        if (linkDefs.has(mark)) {
          usage.set(mark, (usage.get(mark) ?? 0) + 1);
        }
      }
    }

    let changed = false;

    const children = (block.children as ISpanChild[]).map((child) => {
      const marks = child?.marks ?? [];
      const linkMarks = marks.filter((mark) => linkDefs.has(mark));

      if (!linkMarks.length) {
        return child;
      }

      if (!(child.text ?? "").trim()) {
        changed = true;
        return { ...child, marks: marks.filter((m) => !linkDefs.has(m)) };
      }

      if (linkMarks.length === 1) {
        return child;
      }

      // Overlapping link marks: keep one. internalLink beats link; among the
      // same type the mark spanning the fewest children is the most specific.
      const winner = [...linkMarks].sort((a, b) => {
        const typeRank = (m: string) =>
          linkDefs.get(m) === "internalLink" ? 0 : 1;
        return (
          typeRank(a) - typeRank(b) ||
          (usage.get(a) ?? 0) - (usage.get(b) ?? 0) ||
          linkMarks.indexOf(b) - linkMarks.indexOf(a)
        );
      })[0];

      changed = true;
      return {
        ...child,
        marks: marks.filter((m) => !linkDefs.has(m) || m === winner),
      };
    });

    return changed ? { ...block, children } : block;
  }) as PortableTextBlock[];
};
